import { useMemo, useState } from 'react';
import { MipClassifierRes } from '@dealstack/shared/lib/generated';
import { ClassifiersTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/ClassifiersTable/ClassifiersTable.tsx';
import { ManageClassifierDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/ManageClassifierDrawer/ManageClassifierDrawer.tsx';
import { ManageCohortTagsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/ManageCohortTagsDrawer/ManageCohortTagsDrawer.tsx';
import {
	useCreateMipClassifier,
	useMipClassifiersList,
	useUpdateMipClassifier,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers.ts';
import {
	useTagsList,
	useDetachClassifiersFromTag,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useTags.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ArchivedFilter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/utils/types';

export const CohortsTab = () => {
	const { mipId } = useRequiredParams();
	const { hasPermission: canEdit } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: mipId,
		permissionRole: PermissionRole.ADMIN,
	});
	const tMipManage = useTranslationMipManage();
	const [archivedFilter, setArchivedFilter] = useState<ArchivedFilter>('active');
	const [selectedClassifier, setSelectedClassifier] = useState<MipClassifierRes | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [classifierToArchive, setClassifierToArchive] = useState<MipClassifierRes | null>(null);
	const [archiveDidSucceed, setArchiveDidSucceed] = useState(false);
	const [cohortForTags, setCohortForTags] = useState<MipClassifierRes | null>(null);
	const [isTagsDrawerOpen, setIsTagsDrawerOpen] = useState(false);

	const isArchivedParam = archivedFilter === 'all' ? undefined : archivedFilter === 'archived';
	const {
		data: classifiers = [],
		isLoading: isClassifiersLoading,
		isFetching: isClassifiersFetching,
		refetch: refetchClassifiers,
	} = useMipClassifiersList({ mipId, isArchived: isArchivedParam });

	// Active tags drive both the "Tags" column and the per-cohort tag drawer.
	const { data: tags = [] } = useTagsList({ mipId, isArchived: false });

	/** Cohort id → the tags it belongs to, for the "Tags" column. */
	const tagsByClassifierId = useMemo(() => {
		const map: Record<string, { id: string; name: string }[]> = {};
		for (const tag of tags) {
			for (const cohort of tag.classifiers) {
				(map[cohort.id] ??= []).push({ id: tag.id, name: tag.name });
			}
		}
		return map;
	}, [tags]);

	const { mutate: createClassifier, isPending: isCreating } = useCreateMipClassifier({ mipId });
	const { mutate: updateClassifier, isPending: isUpdating } = useUpdateMipClassifier({ mipId });
	const { mutate: detachTag } = useDetachClassifiersFromTag({ mipId });

	const handleRemoveTag = (cohortId: string, tagId: string) => {
		detachTag({ tagId, body: { mipClassifierIds: [cohortId] } });
	};

	const handleEdit = (classifier: MipClassifierRes) => {
		setSelectedClassifier(classifier);
		setIsDrawerOpen(true);
	};

	const handleAdd = () => {
		setSelectedClassifier(null);
		setIsDrawerOpen(true);
	};

	const closeDrawer = () => {
		setIsDrawerOpen(false);
		setSelectedClassifier(null);
	};

	const handleManageTags = (classifier: MipClassifierRes) => {
		setCohortForTags(classifier);
		setIsTagsDrawerOpen(true);
	};

	const closeTagsDrawer = () => {
		setIsTagsDrawerOpen(false);
		setCohortForTags(null);
	};

	const handleSave = ({ mipClassifierId, name }: { mipClassifierId?: string; name: string }) => {
		if (mipClassifierId) {
			updateClassifier(
				{ mipClassifierId, body: { name } },
				{
					onSuccess: () => {
						closeDrawer();
						void refetchClassifiers();
					},
				}
			);
		} else {
			createClassifier(
				{ name },
				{
					onSuccess: () => {
						closeDrawer();
						void refetchClassifiers();
					},
				}
			);
		}
	};

	const handleArchiveToggle = (classifier: MipClassifierRes) => {
		if (!classifier.archived) {
			setArchiveDidSucceed(false);
			setClassifierToArchive(classifier);
			return;
		}
		updateClassifier(
			{ mipClassifierId: classifier.id, body: { archived: false } },
			{ onSuccess: () => refetchClassifiers() }
		);
	};

	const closeArchiveConfirm = () => {
		setClassifierToArchive(null);
		setArchiveDidSucceed(false);
	};

	const handleConfirmArchive = () => {
		if (!classifierToArchive) {
			return;
		}
		updateClassifier(
			{ mipClassifierId: classifierToArchive.id, body: { archived: true } },
			{
				onSuccess: () => {
					setArchiveDidSucceed(true);
					void refetchClassifiers();
				},
			}
		);
	};

	const isLoading = isClassifiersLoading || isClassifiersFetching || isCreating || isUpdating;

	return (
		<>
			<ClassifiersTable
				classifiers={classifiers}
				isLoading={isLoading}
				canEdit={canEdit}
				archivedFilter={archivedFilter}
				onArchivedFilterChange={setArchivedFilter}
				onEdit={handleEdit}
				onAdd={handleAdd}
				onArchiveToggle={handleArchiveToggle}
				onManageTags={handleManageTags}
				onRemoveTag={handleRemoveTag}
				tagsByClassifierId={tagsByClassifierId}
			/>

			<ManageClassifierDrawer
				opened={isDrawerOpen}
				onClose={closeDrawer}
				classifier={selectedClassifier}
				onSave={handleSave}
				isLoading={isCreating || isUpdating}
			/>

			<ManageCohortTagsDrawer
				opened={isTagsDrawerOpen}
				onClose={closeTagsDrawer}
				cohort={cohortForTags}
				mipId={mipId}
				allTags={tags}
				onSaved={closeTagsDrawer}
			/>

			<ConfirmDeleteDrawer
				opened={!!classifierToArchive}
				onClose={closeArchiveConfirm}
				title={tMipManage('root.classifiers.archive_confirm.title')}
				confirmDeleteText={classifierToArchive?.name ?? ''}
				wordMismatchErrorMessage={tMipManage(
					'root.classifiers.archive_confirm.word_mismatch'
				)}
				inputLabel={tMipManage('root.classifiers.archive_confirm.input_label', {
					name: classifierToArchive?.name ?? '',
				})}
				inputPlaceholder={classifierToArchive?.name ?? ''}
				deleteBtnText={tMipManage('root.classifiers.archive_confirm.confirm_button')}
				cancelBtnText={tMipManage('root.classifiers.archive_confirm.cancel_button')}
				deleteSuccess={archiveDidSucceed}
				deleteLoading={isUpdating}
				handleDelete={handleConfirmArchive}
				enableDeleteButtonOnlyOnConfirmDelete
			/>
		</>
	);
};
